import createClient from "openapi-fetch";
import type {components, paths} from "../../../api/generated/schema";

export type RegisterInput = components["schemas"]["RegisterInput"];
export type LoginInput = components["schemas"]["LoginInput"];
export type CaptchaResponse = components["schemas"]["CaptchaResponse"];
export type BrowserTokens = components["schemas"]["BrowserTokens"];
export type BrowserRegisterResult = components["schemas"]["BrowserRegisterResult"];
export type PhoneVerificationRequestInput = components["schemas"]["PhoneVerificationRequestInput"];
export type PhoneVerificationConfirmInput = components["schemas"]["PhoneVerificationConfirmInput"];
export type PhoneVerificationChallenge = components["schemas"]["PhoneVerificationChallenge"];
export type PasswordResetRequestInput = components["schemas"]["PasswordResetRequestInput"];
// Keep exact-one proof semantics in TypeScript as well as OpenAPI. A plain
// object union accepts a literal containing fields from both members because
// every key belongs to some union branch; explicit never fields close that
// structural-typing loophole.
export type PasswordResetCompleteInput =
  | (components["schemas"]["PasswordResetEmailCompleteInput"] & {login?: never; code?: never; captcha?: never})
  | (components["schemas"]["PasswordResetPhoneCompleteInput"] & {token?: never});
export type Me = components["schemas"]["Me"];
type ErrorEnvelope = components["schemas"]["Error"];

const client = createClient<paths>({baseUrl: "/api/v1", credentials: "same-origin"});

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly fields: Record<string, string>;

  constructor(status: number, envelope?: ErrorEnvelope) {
    const detail = envelope?.error;
    const code = detail?.code ?? "network_error";
    // The server message is not presentation text: services currently return
    // English domain details. UI maps this stable code to the RU catalog and
    // keeps request_id for support without leaking raw transport wording.
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = detail?.request_id ?? "";
    this.fields = detail?.fields ?? {};
  }
}

export class OperationKey {
  private fingerprint = "";
  private key = "";

  forPayload(payload: unknown): string {
    const next = stableStringify(payload);
    if (next !== this.fingerprint) {
      this.fingerprint = next;
      this.key = crypto.randomUUID();
    }
    return this.key;
  }
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "undefined";
}

export async function retryNetwork<T>(run: (key: string) => Promise<T>, operation: OperationKey, payload: unknown): Promise<T> {
  const key = operation.forPayload(payload);
  try {
    return await run(key);
  } catch (error) {
    if (!(error instanceof TypeError)) {
      throw error;
    }
    return run(key);
  }
}

function throwResponse(error: unknown, response: Response): never {
  throw new ApiError(response.status, error as ErrorEnvelope | undefined);
}

export async function browserRegister(input: RegisterInput, operation: OperationKey): Promise<BrowserRegisterResult> {
  return retryNetwork(async (key) => {
    const {data, error, response} = await client.POST("/auth/browser/register", {body: input, headers: {"Idempotency-Key": key}});
    if (!data) throwResponse(error, response);
    return data;
  }, operation, input);
}

export async function browserLogin(input: LoginInput, operation: OperationKey): Promise<BrowserTokens> {
  return retryNetwork(async (key) => {
    const {data, error, response} = await client.POST("/auth/browser/login", {
      body: input,
      params: {header: {"Idempotency-Key": key}},
    });
    if (!data) throwResponse(error, response);
    return data;
  }, operation, credentialOperationPayload(input));
}

export async function browserConfirmEmail(token: string, operation: OperationKey): Promise<BrowserTokens> {
  const input = {token};
  return retryNetwork(async (key) => {
    const {data, error, response} = await client.POST("/auth/browser/email/confirm", {body: input, headers: {"Idempotency-Key": key}});
    if (!data) throwResponse(error, response);
    return data;
  }, operation, input);
}

export async function requestPhoneVerification(
  accessToken: string,
  input: PhoneVerificationRequestInput,
  operation: OperationKey,
): Promise<PhoneVerificationChallenge> {
  return retryNetwork(async (key) => {
    const {data, error, response} = await client.POST("/auth/phone/request", {
      body: input,
      headers: {Authorization: `Bearer ${accessToken}`},
      params: {header: {"Idempotency-Key": key}},
    });
    if (!data) throwResponse(error, response);
    return data;
  }, operation, input);
}

export async function confirmPhoneVerification(
  accessToken: string,
  input: PhoneVerificationConfirmInput,
  operation: OperationKey,
): Promise<void> {
  return retryNetwork(async (key) => {
    const {error, response} = await client.POST("/auth/phone/confirm", {
      body: input,
      headers: {Authorization: `Bearer ${accessToken}`},
      params: {header: {"Idempotency-Key": key}},
    });
    if (response.status !== 204) throwResponse(error, response);
  }, operation, credentialOperationPayload(input));
}

export async function requestPasswordReset(
  input: PasswordResetRequestInput,
  operation: OperationKey,
): Promise<void> {
  return retryNetwork(async (key) => {
    const {error, response} = await client.POST("/auth/password-reset/request", {
      body: input,
      params: {header: {"Idempotency-Key": key}},
    });
    if (response.status !== 202) throwResponse(error, response);
  }, operation, input);
}

export async function completePasswordReset(
  input: PasswordResetCompleteInput,
  operation: OperationKey,
): Promise<void> {
  return retryNetwork(async (key) => {
    const {error, response} = await client.POST("/auth/password-reset/complete", {
      body: input,
      params: {header: {"Idempotency-Key": key}},
    });
    if (response.status !== 204) throwResponse(error, response);
  }, operation, credentialOperationPayload(input));
}

// CAPTCHA tokens are one-shot transport data. They are deliberately excluded
// from the operation fingerprint so neither OperationKey nor downstream
// idempotency identity is derived from a raw provider token.
export function credentialOperationPayload(input: object): Record<string, unknown> {
	const credential = {...input} as Record<string, unknown>;
	delete credential.captcha;
  return credential;
}

export async function browserRefresh(): Promise<BrowserTokens> {
  const {data, error, response} = await client.POST("/auth/browser/refresh", {headers: {"Idempotency-Key": crypto.randomUUID()}});
  if (!data) throwResponse(error, response);
  return data;
}

export async function browserLogout(): Promise<void> {
  const {error, response} = await client.POST("/auth/browser/logout", {headers: {"Idempotency-Key": crypto.randomUUID()}});
  if (response.status !== 204) throwResponse(error, response);
}

export async function getMe(accessToken: string): Promise<Me> {
  const {data, error, response} = await client.GET("/users/me", {headers: {Authorization: `Bearer ${accessToken}`}});
  if (!data) throwResponse(error, response);
  return data;
}
